import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { API, apiFetch, cachedFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { SweepStat } from "./_sweep_stat.jsx";

export function TemplateSweepPage({ showNotification }) {
  // Matter picker state
  const [matters, setMatters] = useState([]);
  const [mattersLoading, setMattersLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMatter, setSelectedMatter] = useState(null);

  // Sweep state
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("all"); // all | issues | ok

  // Load matters list on mount (same endpoint MattersPage uses).
  useEffect(() => {
    cachedFetch("/api/matters")
      .then(data => { setMatters(data.data || []); setMattersLoading(false); })
      .catch(err => {
        setMattersLoading(false);
        showNotification && showNotification(`Failed to load matters: ${err.message}`, "error");
      });
  }, []);

  // Filter the picker by client name OR display number (case-insensitive).
  // Cap at 30 results to keep the dropdown manageable.
  const filteredMatters = matters
    .filter(m => {
      if (!search) return true;
      const q = search.toLowerCase();
      const name = (m.client?.name || "").toLowerCase();
      const num = (m.display_number || "").toLowerCase();
      return name.includes(q) || num.includes(q);
    })
    .slice(0, 30);

  const runSweep = async () => {
    if (!selectedMatter) {
      showNotification && showNotification("Pick a matter from the list first", "error");
      return;
    }
    setRunning(true);
    setReport(null);
    try {
      const data = await apiFetch("/api/admin/template-sweep", {
        method: "POST",
        body: JSON.stringify({ matter_id: selectedMatter.id }),
      });
      setReport(data);
      showNotification && showNotification(
        `Sweep complete: ${data.summary.ok} clean, ${data.summary.unfilled} unfilled, ${data.summary.error} errored`,
        data.summary.error > 0 || data.summary.unfilled > 0 ? "warning" : "success",
      );
    } catch (e) {
      showNotification && showNotification(`Sweep failed: ${e.message}`, "error");
    } finally {
      setRunning(false);
    }
  };

  const clearAll = () => {
    setReport(null);
    setSelectedMatter(null);
    setSearch("");
    setFilter("all");
  };

  const downloadUrl = (filename) => {
    if (!report?.sweep_id) return "#";
    return `${API}/api/admin/template-sweep/${report.sweep_id}/download/${encodeURIComponent(filename)}`;
  };

  const filteredRows = report ? report.rows.filter(r => {
    if (filter === "issues") return r.status !== "ok";
    if (filter === "ok") return r.status === "ok";
    return true;
  }) : [];

  const statusBadge = (status, unfilledCount) => {
    if (status === "ok") {
      return React.createElement("span", {
        style: {
          display: "inline-block", padding: "3px 8px", borderRadius: "10px",
          background: C.successBg, color: C.success, fontSize: "11px", fontWeight: 600,
        },
      }, "✓ OK");
    }
    if (status === "unfilled") {
      return React.createElement("span", {
        style: {
          display: "inline-block", padding: "3px 8px", borderRadius: "10px",
          background: `${C.gold}25`, color: C.goldDim, fontSize: "11px", fontWeight: 600,
        },
      }, `Unfilled × ${unfilledCount}`);
    }
    return React.createElement("span", {
      style: {
        display: "inline-block", padding: "3px 8px", borderRadius: "10px",
        background: C.errorBg, color: C.error, fontSize: "11px", fontWeight: 600,
      },
    }, "✗ Error");
  };

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "1200px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "24px" } },
      React.createElement("h1", { style: { margin: 0, color: C.navy, fontSize: "24px", fontWeight: 700 } }, "Template Verifier"),
      React.createElement("p", { style: { margin: "6px 0 0", color: C.textMuted, fontSize: "14px", maxWidth: "780px", lineHeight: 1.5 } },
        "Generates every registered template against a real Clio matter and reports which ones " +
        "fill cleanly vs. which would ship with unfilled merge tokens. Use this as a final sanity " +
        "check before going live with a client matter. Read-only — nothing is uploaded to Clio.",
      ),
    ),

    // Matter picker + Run button card
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${C.warmGray}`,
      },
    },
      // Step 1: Pick a matter
      React.createElement("div", { style: { marginBottom: "16px" } },
        React.createElement("label", {
          style: { display: "block", fontSize: "12px", fontWeight: 600, color: C.navy, marginBottom: "8px" },
        }, "1. Pick a matter to test against"),
        // Search input
        React.createElement("div", { style: { position: "relative", marginBottom: "10px" } },
          React.createElement("span", {
            style: { position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: C.textLight },
          }, I.search),
          React.createElement("input", {
            type: "text",
            placeholder: mattersLoading ? "Loading matters..." : "Search by client name or matter #...",
            value: search, onChange: e => setSearch(e.target.value),
            disabled: mattersLoading || running,
            style: {
              width: "100%", padding: "10px 14px 10px 40px", borderRadius: "8px",
              border: `1px solid ${C.warmGray}`, fontSize: "14px", background: "#fff",
            },
          }),
        ),
        // Selected pill OR results list
        selectedMatter ? React.createElement("div", {
          style: {
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 14px", borderRadius: "8px",
            background: `${C.gold}15`, border: `1px solid ${C.gold}60`,
          },
        },
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: "14px", fontWeight: 600, color: C.navy } },
              selectedMatter.client?.name || "(no client name)"),
            React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, marginTop: "2px" } },
              `Matter #${selectedMatter.display_number || "—"} · Clio ID ${selectedMatter.id} · ${selectedMatter.status || "—"}`),
          ),
          React.createElement("button", {
            onClick: () => { setSelectedMatter(null); setSearch(""); },
            disabled: running,
            style: {
              background: "transparent", border: "none", color: C.textMuted,
              fontSize: "13px", cursor: running ? "not-allowed" : "pointer", padding: "4px 8px",
            },
          }, "Change"),
        ) :
        // Results list
        React.createElement("div", {
          style: {
            maxHeight: "260px", overflowY: "auto",
            border: `1px solid ${C.warmGray}`, borderRadius: "8px", background: "#fff",
          },
        },
          mattersLoading ? React.createElement("div", {
            style: { padding: "30px", textAlign: "center", color: C.textMuted, fontSize: "13px" },
          }, "Loading matters...") :
          filteredMatters.length === 0 ? React.createElement("div", {
            style: { padding: "30px", textAlign: "center", color: C.textMuted, fontSize: "13px" },
          }, search ? "No matches." : "No matters returned by Clio.") :
          filteredMatters.map((m, i) => React.createElement("button", {
            key: m.id, onClick: () => setSelectedMatter(m),
            style: {
              width: "100%", display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr",
              padding: "10px 14px", border: "none", background: "transparent",
              cursor: "pointer", textAlign: "left", alignItems: "center",
              borderBottom: i < filteredMatters.length - 1 ? `1px solid ${C.warmGray}40` : "none",
              transition: "background 0.15s",
            },
            onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
            onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
          },
            React.createElement("div", { style: { fontSize: "13px", color: C.navy, fontWeight: 500 } },
              m.client?.name || "(no client name)"),
            React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, fontFamily: "monospace" } },
              m.display_number || "—"),
            React.createElement("div", { style: { fontSize: "11px", color: C.textMuted } },
              m.status || "—"),
          )),
          // Truncation hint if applicable
          (!mattersLoading && matters.length > 30 && filteredMatters.length >= 30) ? React.createElement("div", {
            style: { padding: "8px 14px", fontSize: "11px", color: C.textMuted, background: C.warmWhite, textAlign: "center" },
          }, "Showing first 30 — refine search to see more") : null,
        ),
      ),

      // Step 2: Run button
      React.createElement("div", {
        style: {
          display: "flex", gap: "12px", alignItems: "center", paddingTop: "12px",
          borderTop: `1px solid ${C.warmGray}`,
        },
      },
        React.createElement("div", { style: { fontSize: "12px", fontWeight: 600, color: C.navy, marginRight: "auto" } },
          "2. Run the sweep"),
        report ? React.createElement("button", {
          onClick: clearAll, disabled: running,
          style: {
            padding: "10px 16px", borderRadius: "8px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.navy, fontSize: "13px", cursor: "pointer",
          },
        }, "Clear") : null,
        React.createElement("button", {
          onClick: runSweep, disabled: running || !selectedMatter,
          style: {
            padding: "10px 24px", borderRadius: "8px", border: "none",
            background: !running && selectedMatter ? C.gold : C.warmGray,
            color: "#fff", fontSize: "14px", fontWeight: 600,
            cursor: !running && selectedMatter ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", gap: "8px",
          },
        },
          running ? React.createElement(Spinner, { size: 14, color: "#fff" }) : null,
          running ? "Running sweep..." : "Run Sweep",
        ),
      ),
    ),

    // While running
    running ? React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "32px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${C.warmGray}`,
        textAlign: "center", color: C.textMuted,
      },
    },
      React.createElement("div", { style: { marginBottom: "12px" } },
        React.createElement(Spinner, { size: 28, color: C.gold })
      ),
      React.createElement("div", { style: { fontSize: "14px" } },
        "Pulling matter data from Clio and rendering ~136 documents — about 30 seconds…"),
    ) : null,

    // Report
    report ? React.createElement(React.Fragment, null,
      // Summary card
      React.createElement("div", {
        style: {
          background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${C.warmGray}`,
        },
      },
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginBottom: "6px" } },
          `Matter ${report.matter_id} — ${report.matter_display}`),
        React.createElement("div", {
          style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "16px" },
        },
          React.createElement(SweepStat, { label: "Total Runs", value: report.summary.total, color: C.navy }),
          React.createElement(SweepStat, { label: "Clean", value: report.summary.ok, color: C.success }),
          React.createElement(SweepStat, { label: "Unfilled", value: report.summary.unfilled, color: C.goldDim }),
          React.createElement(SweepStat, { label: "Errors", value: report.summary.error, color: C.error }),
          React.createElement(SweepStat, { label: "Templates Tested", value: report.summary.templates_tested, color: C.navy }),
          React.createElement(SweepStat, { label: "Attorney Passes", value: report.summary.attorney_passes, color: C.navy }),
        ),
      ),

      // Filter chips
      React.createElement("div", {
        style: { display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" },
      },
        ["all", "issues", "ok"].map(f =>
          React.createElement("button", {
            key: f, onClick: () => setFilter(f),
            style: {
              padding: "6px 14px", borderRadius: "16px", fontSize: "12px", fontWeight: 600,
              cursor: "pointer", border: `1px solid ${filter === f ? C.navy : C.warmGray}`,
              background: filter === f ? C.navy : "#fff",
              color: filter === f ? "#fff" : C.navy,
            },
          }, f === "all" ? `All (${report.rows.length})`
            : f === "issues" ? `Issues (${report.summary.unfilled + report.summary.error})`
            : `Clean (${report.summary.ok})`),
        ),
      ),

      // Results table
      React.createElement("div", {
        style: {
          background: "#fff", borderRadius: "12px", overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${C.warmGray}`,
        },
      },
        filteredRows.length === 0 ? React.createElement("div", {
          style: { padding: "40px", textAlign: "center", color: C.textMuted },
        }, "No rows match this filter.") :
        React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
          React.createElement("thead", null,
            React.createElement("tr", { style: { background: C.warmWhite } },
              ["Template", "Category", "Attorney Pass", "Status", "Detail", ""].map((h, i) =>
                React.createElement("th", {
                  key: i,
                  style: {
                    padding: "10px 16px", textAlign: "left",
                    color: C.textMuted, fontWeight: 600, fontSize: "11px",
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  },
                }, h),
              ),
            ),
          ),
          React.createElement("tbody", null,
            ...filteredRows.map((r, idx) =>
              React.createElement("tr", {
                key: r.filename,
                style: { borderTop: `1px solid ${C.warmGray}40` },
              },
                React.createElement("td", { style: { padding: "10px 16px", color: C.navy, fontSize: "13px" } },
                  React.createElement("div", { style: { fontWeight: 600 } }, r.template_label),
                  React.createElement("div", {
                    style: { fontFamily: "monospace", fontSize: "11px", color: C.textMuted, marginTop: "2px" },
                  }, r.template),
                ),
                React.createElement("td", {
                  style: { padding: "10px 16px", color: C.textMuted, fontSize: "12px" },
                }, r.category),
                React.createElement("td", { style: { padding: "10px 16px", color: C.textDark, fontSize: "12px" } },
                  r.attorney_name),
                React.createElement("td", { style: { padding: "10px 16px" } },
                  statusBadge(r.status, (r.unfilled || []).length)),
                React.createElement("td", {
                  style: { padding: "10px 16px", color: C.textMuted, fontSize: "11px", maxWidth: "320px" },
                },
                  r.status === "error" ? r.error :
                  r.status === "unfilled" ? (r.unfilled.slice(0, 4).map(t => `{{${t}}}`).join(", ") +
                    (r.unfilled.length > 4 ? ` …(+${r.unfilled.length - 4})` : "")) :
                  `${Math.round((r.size_bytes || 0) / 1024)} KB`,
                ),
                React.createElement("td", { style: { padding: "10px 16px", textAlign: "right" } },
                  r.size_bytes > 0 ? React.createElement("a", {
                    href: downloadUrl(r.filename),
                    download: r.filename,
                    style: {
                      color: C.gold, fontSize: "12px", fontWeight: 600,
                      textDecoration: "none",
                    },
                  }, "Download") : null,
                ),
              ),
            ),
          ),
        ),
      ),

      // Footer note
      React.createElement("div", {
        style: { marginTop: "16px", fontSize: "11px", color: C.textMuted, lineHeight: 1.5 },
      },
        "Generated outputs live in memory for 1 hour after the sweep. " +
        "Download anything you want to keep, or re-run the sweep to refresh."),
    ) : null,
  );
}

